import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import { useState, useEffect } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import DialogText from './DialogText';
import { color } from '../../app/theme';
import Tag from './Tag';
import IcSunSVG from '../../assets/images/svg/ic-sun.svg';
import IcArrowLeft from '../../assets/images/svg/ic-arrow-left.svg';
import IcArrowRight from '../../assets/images/svg/ic-arrow-right.svg';
import IconAddOrg from '../../assets/images/Icons/IconAddOrg';
import IcMoreHorizontal from '../../assets/images/svg/ic-more-horizontal.svg';
import IcHelpCircle from '../../assets/images/svg/ic-help-circle.svg';
import TextFieldCardURL from './TextFieldCardURL';
import UploadImage from './UploadImage';
import TextFieldCardCounter from './TextFieldCardCounter';
import TextAreaCard from './TextAreaCard';
import OptionalCardButton from './OptionalCardButton';
import DatePicker from './DatePicker';
import TimePicker from './TimePicker';
import MainService from '../../services/MainService';
import Tooltip from './Tooltip';
import config from '../../app/config';
import {
  validateHttpsPath,
  validateHttpsAndHttpPath,
  validateNo8Path,
  validatePhone,
} from '../../app/utils';
import Button from './Button';
import { saveDefaultTemplate } from '../../features/organizationSlice';
import { setState, setDefaultTemplate } from '../../features/ecBindingSlice';
import CONSTANTS from '../../app/constants';

const useStyles = makeStyles(
  theme => ({
    contentContainer: {
      height: 651,
      padding: '0 36px 24px',
      overflow: 'auto',
    },
    content: {},
    cardContainer: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '0 0 8px 0',
      margin: '0 0 16px 0',
      '& > div:last-child': {
        marginRight: 0,
      },
    },
    card: {
      position: 'relative',
      flex: '0 0 auto',
      width: 204,
      borderRadius: 12,
      boxShadow: '0 8px 36px 0 rgba(0, 0, 0, 0.06)',
      backgroundColor: color.$7,
      padding: '12px 0 16px',
      margin: '36px 20px 0 0',
    },
    cardHeader: {
      padding: '0 12px',
    },
    addCard: {
      cursor: 'pointer',
      flex: '0 0 auto',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: 205,
      height: 205,
      borderRadius: 12,
      boxShadow: '0 8px 36px 0 rgba(0, 0, 0, 0.06)',
      backgroundColor: color.$7,
      margin: '28px 20px 0 0',
    },
    moveCard: {
      margin: '0 0 8px 0',
      '& > img': {
        cursor: 'pointer',
        display: 'inline-block',
        '&:first-child': {
          marginRight: 8,
        },
      },
    },
    crawlContainer: {
      padding: '0 12px',
      marginBottom: 8,
    },
    uploadImageContainer: {
      padding: '0 12px',
      marginBottom: 8,
    },
    tipContainer: {
      display: 'flex',
      alignItems: 'center',
      fontSize: 13,
      lineHeight: '19px',
      color: color.$13,
    },
    titleContainer: {
      padding: '0 12px',
      margin: '16px 0 8px',
    },
    descriptionContainer: {
      padding: '0 12px',
      marginBottom: 8,
    },
    divide: {
      margin: '16px 0 4px 0',
      border: 'none',
      borderTop: `1px solid ${color.$9}`,
    },
    'divide--margin-L': {
      margin: '16px 0',
    },
    addButtonContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0 16px',
    },
    addButton: {
      ...theme.link.primaryGreen.default,
      display: 'block',
      fontSize: 13,
      lineHeight: 1,
      margin: '16px 0 0 0',
    },
    moreIcon: {
      cursor: 'pointer',
      position: 'absolute',
      top: -12,
      right: -12,
      zIndex: 1,
      width: 24,
      height: 24,
      borderRadius: '50%',
      backgroundColor: `${color.$10}`,
    },
    moreContainer: showMore => ({
      position: 'absolute',
      top: 28,
      right: 0,
      zIndex: 1,
      display: showMore ? 'block' : 'none',
      width: 160,
      padding: '8px 0',
      backgroundColor: color.$7,
      borderRadius: 12,
      boxShadow:
        '0 16px 48px 0 rgba(0, 0, 0, 0.07), 0 6px 18px 0 rgba(0, 0, 0, 0.03)',
    }),
    moreItem: {
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      height: 44,
      fontSize: 13,
      lineHeight: '18px',
      padding: '0 24px',
      '&:hover': {
        color: color.$2,
      },
      '& > img': {
        display: 'block',
        margin: '0 0 0 8px',
      },
    },
    tooltip: {
      fontSize: 13,
      color: color.$12,
    },
    tooltipPopper: {
      zIndex: 5000,
    },
    selectTemplateBlock: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: 24,
    },
    selectTemplate: {
      ...theme.link.primaryGreen.default,
      margin: '0 0 0 4px',
    },
    actionContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'end',
    },
    saveTemplateBlock: {
      '& > div': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        height: 44,
        marginTop: 12,
      },
    },
    date: {
      width: 180,
    },
    time: {
      width: 100,
      marginLeft: 8,
    },
    saveTemplateFormControlLabel: {
      flex: '0 0 auto',
    },
    saveTemplateLabel: {
      fontSize: 15,
      lineHeight: 1,
      color: color.$13,
    },
    saveTemplateName: {
      width: 265,
    },
    opertationBlock: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    errorMessage: {
      fontSize: 13,
      color: color.$17,
      textAlign: 'right',
      margin: '0 0 16px 0',
    },
    templateTitle: {
      marginRight: 12,
      fontSize: 15,
      color: color.$3,
      display: 'flex',
      alignItems: 'center',
      '& > img': {
        marginLeft: 6,
      },
    },
    expiredAt: {
      marginRight: 0,
    },
  }),
  {
    name: 'OptionalCardDialog',
  },
);

function createDefaultData() {
  return {
    id: uuidv4(),
    goodsURL: '',
    imageType: 'upload',
    imageUrl: '',
    title: '',
    subtitle: '',
    buttons: [
      {
        id: uuidv4(),
        type: CONSTANTS.TEMPLATE_BUTTON_TYPE.POSTBACK,
        title: '',
        data: '',
        tags: [],
      },
    ],
  };
}

function MoreIcon({ onFocus, onDelete }) {
  const [showMore, setShowMore] = useState(false);
  const classes = useStyles(showMore);
  const { t } = useTranslation();

  return (
    <ClickAwayListener onClickAway={() => setShowMore(false)}>
      <div
        className={classes.moreIcon}
        onClick={() => {
          setShowMore(!showMore);
        }}
      >
        <img src={IcMoreHorizontal} alt="more" />
        <div className={classes.moreContainer}>
          <div className={classes.moreItem} onClick={onFocus}>
            {t('templates.import-url')}
            <Tooltip
              placement="right"
              light
              titleClass={classes.tooltip}
              title={t('templates.import-url-tooltip')}
              detailClasses={{
                popper: classes.tooltipPopper,
              }}
            >
              <img src={IcHelpCircle} alt="import goods" />
            </Tooltip>
          </div>
          <div className={classes.moreItem} onClick={onDelete}>
            {t('templates.delete-card')}
          </div>
        </div>
      </div>
    </ClickAwayListener>
  );
}

function OptionalCardDialog({
  name,
  templateExpiredAt,
  templateId,
  data,
  onSubmit,
  onCancel,
  messageIndex,
  disableTemplate,
  filterName,
  isShareLink,
  isShowRanking,
  isCampaign,
  isMessageCenter,
  isSendLoading,
  isShowSubmit,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { objectId: orgId } = useSelector(store => store.organization);
  const isSaveAsDefaultTemplate = useSelector(
    store => store.ecBinding.isSaveAsDefaultTemplate,
  );
  const [isModified, setIsModified] = useState(false);
  const [cardData, setCardData] = useState(
    data.length > 0
      ? data.map(d => ({
          id: uuidv4(),
          ...d,
          buttons: d.buttons.map(button => ({ id: uuidv4(), ...button })),
        }))
      : [createDefaultData()],
  );
  const [templateName, setTemplateName] = useState(
    name ||
      `${t('types.message-template-type.card')} ${moment().format(
        'YYYY-MM-DD HH:mm',
      )}`,
  );
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [expiredAt, setExpiredAt] = useState(templateExpiredAt);

  useEffect(() => {
    return () => {
      dispatch(
        setState({
          isSaveAsDefaultTemplate: false,
        }),
      );
    };
  }, []);

  function validateCardData() {
    let totalImages = 0;
    let totalButtons = 0;
    const result = {
      isValid: false,
      message: '',
    };
    for (let i = 0; i < cardData.length; i += 1) {
      const d = cardData[i];

      if (d.imageType === 'url' && !validateHttpsPath(d.imageUrl)) {
        result.message = t(
          'templates.error-message.image-card-image-not-https-url',
        );
        return result;
      }

      if (
        d.title === '' ||
        d.title.replace(filterName, '').length > config.MAX_CARD_TITLE_LENGTH
      )
        return result;
      if (
        d.subtitle === '' ||
        d.subtitle.replace(filterName, '').length >
          config.MAX_CARD_SUBTITLE_LENGTH
      )
        return result;
      totalImages += d.imageUrl !== '' ? 1 : 0;
      totalButtons += d.buttons.length;

      for (
        let buttonIndex = 0;
        buttonIndex < d.buttons.length;
        buttonIndex += 1
      ) {
        const button = d.buttons[buttonIndex];
        if (
          button.title === '' ||
          button.title.length > config.MAX_BUTTON_TITLE_LENGTH
        )
          return result;
        if (button.data === '' && button.type !== 'location') return result;
        if (
          button.type === CONSTANTS.TEMPLATE_BUTTON_TYPE.URL &&
          !validateHttpsAndHttpPath(button.data) &&
          !validateNo8Path(button.data)
        ) {
          result.message = t('templates.error-message.not-https-url');
          return result;
        }
        if (button.type === 'phone' && !validatePhone(button.data)) {
          result.message = t('templates.error-message.not-accept-phone');
          return result;
        }
      }
    }
    if (totalImages !== 0 && totalImages !== cardData.length) {
      result.message = t('templates.error-message.all-same-image-number');
      return result;
    }
    if (totalButtons / cardData[0].buttons.length !== cardData.length) {
      result.message = t('templates.error-message.all-same-button-number');
      return result;
    }
    result.isValid = true;
    return result;
  }

  function handleAddCard() {
    setCardData([...cardData, createDefaultData()]);
  }

  function handleMoveCardLeft(cardIndex) {
    if (cardIndex !== 0 && cardData.length > 1) {
      const temp = { ...cardData[cardIndex] };
      cardData[cardIndex] = { ...cardData[cardIndex - 1] };
      cardData[cardIndex - 1] = temp;
      setCardData([...cardData]);
    }
  }

  function handleMoveCardRight(cardIndex) {
    if (cardIndex !== cardData.length - 1 && cardData.length > 1) {
      const temp = { ...cardData[cardIndex] };
      cardData[cardIndex] = { ...cardData[cardIndex + 1] };
      cardData[cardIndex + 1] = temp;
      setCardData([...cardData]);
    }
  }

  async function handleChangeGoodsURL(url, cardIndex) {
    cardData[cardIndex].goodsURL = url;
    if (/^https?:\/\//.test(url)) {
      const goodsData = await MainService.crawl(url);
      cardData[cardIndex] = {
        ...cardData[cardIndex],
        ...goodsData,
        imageType: 'url',
        aspectRatio: '1:1',
      };
    }
    setCardData([...cardData]);
    setIsModified(true);
  }

  function handleChangeUploadImage(imageData, cardIndex) {
    cardData[cardIndex].imageType = imageData.imageType;
    cardData[cardIndex].imageUrl = imageData.imageUrl;
    cardData[cardIndex].aspectRatio = imageData.aspectRatio;
    setCardData([...cardData]);
    setIsModified(true);
  }

  function handleChangeTitle(title, cardIndex) {
    cardData[cardIndex].title = title;
    setCardData([...cardData]);
    setIsModified(true);
  }

  function handleChangeDescription(subtitle, cardIndex) {
    cardData[cardIndex].subtitle = subtitle;
    setCardData([...cardData]);
    setIsModified(true);
  }

  function handleAddButton(cardIndex) {
    cardData[cardIndex].buttons = [
      ...cardData[cardIndex].buttons,
      {
        id: uuidv4(),
        type: 'postback',
        title: '',
        data: '',
        tags: [],
      },
    ];
    setCardData([...cardData]);
  }

  function handleChangeButton(buttonData, buttonId, cardIndex) {
    cardData[cardIndex].buttons = cardData[cardIndex].buttons.map(button => {
      if (button.id === buttonId) {
        return {
          id: buttonId,
          ...buttonData,
        };
      }
      return button;
    });
    setCardData([...cardData]);
    setIsModified(true);
  }

  function handleDeleteButton(buttonId, cardIndex) {
    cardData[cardIndex].buttons = cardData[cardIndex].buttons.filter(
      button => button.id !== buttonId,
    );
    setCardData([...cardData]);
    setIsModified(true);
  }

  function handleSubmit() {
    onSubmit(cardData, templateName);
  }

  function handleFocusGoodsURL(inputRef) {
    inputRef.focus();
  }

  function handleDeleteCard(cardId) {
    if (cardData.length > 1) {
      const tempCardData = cardData.filter(card => card.id !== cardId);
      setCardData(tempCardData);
    }
  }

  async function handleSave() {
    setIsSaveLoading(true);
    await dispatch(
      saveDefaultTemplate(
        orgId,
        templateId,
        'card',
        templateName,
        cardData,
        expiredAt,
      ),
    );
    setIsSaveLoading(false);
    onCancel();
    dispatch(
      setDefaultTemplate({
        isShow: true,
        templateType: 'card',
        messageIndex,
      }),
    );
  }

  async function handleSaveAndUse() {
    setIsSaveLoading(true);
    await dispatch(
      saveDefaultTemplate(
        orgId,
        templateId,
        'card',
        templateName,
        cardData,
        expiredAt,
      ),
    );
    setIsSaveLoading(false);
    onSubmit(cardData, templateName);
    dispatch(
      setDefaultTemplate({
        isShow: false,
        templateType: 'card',
        messageIndex,
      }),
    );
  }

  const cardComponents = cardData.map((d, index) => {
    let goodUrlRef = null;
    const buttonComponents = d.buttons.map((button, idx) => {
      return (
        <div key={`card-${d.id}-button-${button.id}`}>
          <hr
            className={classnames(classes.divide, {
              [classes['divide--margin-L']]: d.buttons.length === 1,
            })}
          />
          <OptionalCardButton
            type={button.type}
            title={button.title}
            data={button.data}
            tags={button.tags}
            onChange={buttonData =>
              handleChangeButton(buttonData, button.id, index)
            }
            showDeleteIcon={d.buttons.length > 1}
            onDelete={() => handleDeleteButton(button.id, index)}
            isModified={isModified}
            isShareLink={isShareLink}
            isShowRanking={isShowRanking}
            isCampaign={isCampaign}
            buttonIndex={idx}
          />
        </div>
      );
    });
    return (
      <div className={classes.card} key={`card-${d.id}`}>
        {!isCampaign && (
          <>
            <MoreIcon
              onFocus={() => handleFocusGoodsURL(goodUrlRef)}
              onDelete={() => handleDeleteCard(d.id)}
            />
            <div className={classes.cardHeader}>
              <div className={classes.moveCard}>
                <img
                  src={IcArrowLeft}
                  alt="Move to left"
                  onClick={() => handleMoveCardLeft(index)}
                />
                <img
                  src={IcArrowRight}
                  alt="Move to right"
                  onClick={() => handleMoveCardRight(index)}
                />
              </div>
            </div>

            <div className={classes.crawlContainer}>
              <TextFieldCardURL
                inputRef={dom => {
                  goodUrlRef = dom;
                }}
                goodsURL={d.goodsURL}
                onChange={url => handleChangeGoodsURL(url, index)}
              />
            </div>
          </>
        )}
        <div className={classes.uploadImageContainer}>
          <UploadImage
            imageType={d.imageType}
            imageUrl={d.imageUrl}
            aspectRatio={d.aspectRatio}
            index={index}
            onChange={imageData => handleChangeUploadImage(imageData, index)}
          />
        </div>
        <hr className={classnames(classes.divide)} />
        <div className={classes.titleContainer}>
          <TextFieldCardCounter
            placeholder={t('templates.title-placeholder')}
            value={d.title}
            onChange={title => handleChangeTitle(title, index)}
            maxCount={40}
            status={(isModified && d.title) === '' ? 'error' : undefined}
            filterName={filterName}
          />
        </div>
        <div className={classes.descriptionContainer}>
          <TextAreaCard
            placeholder={t('templates.description-placeholder')}
            value={d.subtitle}
            onChange={subtitle => handleChangeDescription(subtitle, index)}
            maxCount={60}
            status={(isModified && d.subtitle) === '' ? 'error' : undefined}
            filterName={filterName}
          />
        </div>
        {buttonComponents}
        {buttonComponents.length < 3 && (
          <div className={classes.addButtonContainer}>
            <div
              className={classes.addButton}
              onClick={() => handleAddButton(index)}
            >
              {t('templates.add-card-button')}
            </div>
          </div>
        )}
      </div>
    );
  });
  const contentComponent = (
    <div className={classes.content}>
      {!disableTemplate && !isCampaign && !isMessageCenter && (
        <div className={classes.selectTemplateBlock}>
          {`${t('or')} `}
          <span
            className={classes.selectTemplate}
            onClick={() => {
              onCancel();
              dispatch(
                setDefaultTemplate({
                  isShow: true,
                  templateType: 'card',
                  messageIndex,
                }),
              );
            }}
          >
            {t('select-by-default-template')}
          </span>
        </div>
      )}
      <div className={classes.cardContainer}>
        {cardComponents}
        {cardComponents.length < 10 && !isCampaign && (
          <div className={classes.addCard} onClick={handleAddCard}>
            <IconAddOrg id="add-card-icon" width={46.4} height={46.4} />
          </div>
        )}
      </div>
      {!isCampaign && (
        <div className={classes.tipContainer}>
          <Tag icon={IcSunSVG} color="blue" tag={t('templates.tip')} />
          {t('templates.optional-card-tip')}
        </div>
      )}
    </div>
  );
  const { isValid, message: errorMessage } = validateCardData();
  const actionComponent = (
    <div style={{ width: '100%' }}>
      {errorMessage !== '' && (
        <div className={classes.errorMessage}>{errorMessage}</div>
      )}
      <div className={classes.actionContainer}>
        <div className={classes.saveTemplateBlock}>
          {!disableTemplate && !isCampaign && (
            <>
              <div>
                {isMessageCenter ? (
                  <span className={classes.templateTitle}>
                    {t('chat.templateName')}
                  </span>
                ) : (
                  <FormControlLabel
                    className={classes.saveTemplateFormControlLabel}
                    classes={{
                      label: classes.saveTemplateLabel,
                    }}
                    control={
                      <Checkbox
                        checked={isSaveAsDefaultTemplate}
                        onChange={() => {
                          dispatch(
                            setState({
                              isSaveAsDefaultTemplate: !isSaveAsDefaultTemplate,
                            }),
                          );
                        }}
                        name="checkedB"
                        color="primary"
                      />
                    }
                    label={t('save-as-default-template')}
                  />
                )}
                {(isSaveAsDefaultTemplate || isMessageCenter) && (
                  <TextFieldCardCounter
                    className={classes.saveTemplateName}
                    maxCount={0}
                    value={templateName}
                    onChange={v => {
                      setTemplateName(v);
                    }}
                    status={!templateName ? 'error' : undefined}
                    placeholder={t('key-in-template-name')}
                  />
                )}
              </div>
              {(isSaveAsDefaultTemplate || isMessageCenter) && (
                <div>
                  <FormControlLabel
                    className={classnames(
                      classes.saveTemplateFormControlLabel,
                      classes.expiredAt,
                    )}
                    classes={{
                      label: classes.saveTemplateLabel,
                    }}
                    control={
                      <Checkbox
                        checked={!!expiredAt}
                        onChange={e => {
                          if (e.target.checked) {
                            setExpiredAt(
                              moment()
                                .add(
                                  config.DEFAULT_TEMPLATE_EXPIRED_DAY,
                                  'days',
                                )
                                .hour(0)
                                .minute(0),
                            );
                          } else {
                            setExpiredAt(undefined);
                          }
                        }}
                        name="checkedB"
                        color="primary"
                      />
                    }
                    label={
                      <span className={classes.templateTitle}>
                        {t('save-template-expired')}
                        <Tooltip
                          light
                          placement="top"
                          title={t('save-template-expired-tooltip')}
                        >
                          <img
                            className={classes.marginLeft8}
                            src={IcHelpCircle}
                            alt="alert"
                          />
                        </Tooltip>
                      </span>
                    }
                  />
                  {!!expiredAt && (
                    <>
                      <DatePicker
                        className={classes.date}
                        selectedDate={expiredAt}
                        minDate={moment()}
                        modalStyle={{
                          top: -430,
                        }}
                        onChange={date => {
                          setExpiredAt(
                            moment(
                              `${moment(date).format('YYYY-MM-DD')} ${moment(
                                expiredAt,
                              ).format('HH:mm')}`,
                            ),
                          );
                        }}
                        placeholder={t('datepicker.selectDate')}
                      />
                      <TimePicker
                        className={classes.time}
                        selectedTime={expiredAt}
                        isUp
                        onChange={value => {
                          setExpiredAt(
                            moment(
                              `${moment(expiredAt).format(
                                'YYYY-MM-DD',
                              )} ${moment(value).format('HH:mm')}`,
                            ),
                          );
                        }}
                        placeholder={t('datepicker.selectDate')}
                      />
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <div className={classes.opertationBlock}>
          <Button topic="primaryBlue" onClick={onCancel}>
            {t('modal.cancel')}
          </Button>
          {isSaveAsDefaultTemplate || isMessageCenter ? (
            <>
              <Button
                style={{ margin: '0 0 0 8px' }}
                topic="primaryBlue"
                onClick={handleSave}
                isDisable={!isValid || !templateName || templateName === ''}
                isLoading={isSaveLoading}
              >
                {t('modal.save')}
              </Button>
              {isShowSubmit && (
                <Button
                  style={{ margin: '0 0 0 8px' }}
                  topic="primaryBlue"
                  onClick={handleSaveAndUse}
                  isDisable={!isValid || !templateName || templateName === ''}
                  isLoading={isSaveLoading || isSendLoading}
                >
                  {isMessageCenter
                    ? t('modal.save-and-send')
                    : t('modal.save-and-submit')}
                </Button>
              )}
            </>
          ) : (
            <Button
              style={{ margin: '0 0 0 8px' }}
              topic="primaryBlue"
              onClick={handleSubmit}
              isDisable={!isValid}
            >
              {t('modal.sure')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
  return (
    <DialogText
      isOpen
      title={
        isMessageCenter
          ? t('template.create-tooltip')
          : t('templates.optional-card')
      }
      content={contentComponent}
      contentClass={classes.contentContainer}
      actions={actionComponent}
    />
  );
}

OptionalCardDialog.defaultProps = {
  onSubmit: () => {},
  onCancel: () => {},
  filterName: '',
  isShareLink: false, // 是否顯示增粉工具行銷活動訊息內容的選項型卡片
  isShowRanking: false, // 是否顯示增粉工具分享活動者是否接受好友加入會員通知的選項型卡片
  isCampaign: false, // 是否顯示增粉工具的選項型卡片
  isMessageCenter: false, // 是否是訊息中心的選項型卡片
  isSendLoading: false,
  isShowSubmit: true,
  data: [
    {
      goodsURL: '',
      imageType: 'upload',
      imageUrl: '',
      title: '',
      subtitle: '',
      buttons: [
        {
          type: 'postback',
          title: '',
          data: '',
          tags: [],
        },
      ],
    },
  ],
};

OptionalCardDialog.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      goodsURL: PropTypes.string,
      imageType: PropTypes.oneOf(['upload', 'url']),
      imageUrl: PropTypes.string,
    }),
  ),
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  filterName: PropTypes.any,
  isShareLink: PropTypes.bool,
  isShowRanking: PropTypes.bool,
  isCampaign: PropTypes.bool,
  isMessageCenter: PropTypes.bool,
  isSendLoading: PropTypes.bool,
  isShowSubmit: PropTypes.bool,
};

export default OptionalCardDialog;
